import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type Column } from "@/components/data/DataTable";
import { StatusBadge } from "@/components/data/StatusBadge";
import { RelTime } from "@/components/data/RelTime";
import { JsonViewer } from "@/components/data/JsonViewer";
import { LocationBadge, LocationFacts, LocationMap } from "@/components/location/LocationDisplay";
import { useActivity, paginatedItems } from "@/lib/queries";
import { truncate } from "@/lib/format";
import type { ActivityRow } from "@/types";
import { Badge } from "@/components/ui/badge";

// request_log keeps 7 days (cleanupLogs), so there is no longer window.
const SINCE = [
  { v: "1h", label: "1 hour" },
  { v: "24h", label: "24 hours" },
  { v: "7d", label: "7 days" },
];

const PAGE = 50;

export function ActivityPage() {
  const [params, setParams] = useSearchParams();
  // Text inputs are drafts until Apply (or Enter); the query only sees the
  // applied values, and applying any filter goes back to page 1.
  const [pathInput, setPathInput] = useState(params.get("path") ?? "");
  const [userInput, setUserInput] = useState(params.get("user") ?? "");
  const [path, setPath] = useState(pathInput);
  const [user, setUser] = useState(userInput);
  const [status, setStatus] = useState(params.get("status") ?? "all");
  const [since, setSince] = useState(() => {
    const raw = params.get("since") ?? "24h";
    return SINCE.some((s) => s.v === raw) ? raw : "7d";
  });
  const [page, setPage] = useState(0);

  const queryParams = useMemo(
    () => ({
      limit: PAGE,
      offset: page * PAGE,
      path: path.trim() || undefined,
      user_id: user.trim() || undefined,
      status: status === "all" ? undefined : status,
      since,
    }),
    [path, user, status, since, page]
  );

  const apply = (overrides: { status?: string; since?: string } = {}) => {
    const nextStatus = overrides.status ?? status;
    const nextSince = overrides.since ?? since;
    const next = new URLSearchParams();
    if (pathInput.trim()) next.set("path", pathInput.trim());
    if (userInput.trim()) next.set("user", userInput.trim());
    if (nextStatus !== "all") next.set("status", nextStatus);
    if (nextSince !== "24h") next.set("since", nextSince);
    setParams(next, { replace: true });
    setPath(pathInput);
    setUser(userInput);
    setStatus(nextStatus);
    setSince(nextSince);
    setPage(0);
  };

  const q = useActivity(queryParams, { refetchInterval: 10_000 });
  const rows = paginatedItems<ActivityRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<ActivityRow>[] = [
    { key: "time", header: "Time", cell: (r) => <span className="text-xs text-muted-foreground"><RelTime value={r.created_at} /></span>, width: 120 },
    { key: "method", header: "Method", cell: (r) => <span className="font-mono text-xs">{r.method ?? "—"}</span>, width: 70 },
    { key: "path", header: "Path", cell: (r) => <span className="font-mono text-xs">{r.path ?? "—"}</span> },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} />, width: 80 },
    { key: "duration", header: "Duration", cell: (r) => <span className="font-mono text-xs tabular-nums">{r.duration_ms ?? 0} ms</span>, width: 100 },
    {
      key: "user",
      header: "User",
      cell: (r) =>
        r.user_id ? (
          <Link to={`/users/${r.user_id}`} className="font-mono text-xs text-sky-300 hover:underline">
            {r.user_email ?? r.user_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
      width: 200,
    },
    { key: "location", header: "Location", cell: (r) => <LocationBadge location={r} />, width: 180 },
    { key: "ua", header: "User-Agent", cell: (r) => <span className="font-mono text-xs text-muted-foreground">{truncate(r.user_agent ?? "", 60)}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Activity"
        description="Live request log with auto-refresh."
        actions={
          <Badge variant={q.isError ? "error" : q.isFetching ? "info" : "outline"} className="gap-1.5">
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 rounded-full ${q.isError ? "bg-rose-400" : q.isFetching ? "bg-sky-400 animate-pulse" : "bg-emerald-400"}`}
            />
            {q.isError ? "refresh failing" : q.isFetching ? "fetching" : "live · 10s"}
          </Badge>
        }
      />

      <Card className="mb-4 p-3">
        <div className="grid gap-3 sm:grid-cols-[2fr,2fr,1fr,1fr,auto]">
          <Input
            aria-label="Filter by path"
            placeholder="Filter by path"
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply();
            }}
            className="font-mono"
          />
          <Input
            aria-label="Filter by user id"
            placeholder="Filter by user id"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply();
            }}
            className="font-mono"
          />
          <Select value={status} onValueChange={(v) => apply({ status: v })}>
            <SelectTrigger aria-label="Status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="2xx">2xx</SelectItem>
              <SelectItem value="3xx">3xx</SelectItem>
              <SelectItem value="4xx">4xx</SelectItem>
              <SelectItem value="5xx">5xx</SelectItem>
            </SelectContent>
          </Select>
          <Select value={since} onValueChange={(v) => apply({ since: v })}>
            <SelectTrigger aria-label="Time window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SINCE.map((s) => (
                <SelectItem key={s.v} value={s.v}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => apply()}
            className="rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:bg-foreground/90"
          >
            Apply
          </button>
        </div>
      </Card>

      <DataTable
        columns={cols}
        rows={rows}
        loading={q.isLoading}
        error={q.isError ? q.error : undefined}
        onRetry={() => q.refetch()}
        rowKey={(r) => r.id}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE}
        totalRows={total}
        expandable={(r) => (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)]">
            <LocationMap location={r} heightClassName="h-[220px]" />
            <div className="space-y-3">
              <LocationFacts location={r} />
              <JsonViewer
                value={{
                  id: r.id,
                  user_id: r.user_id,
                  ip: r.ip,
                  country: r.country,
                  location_country: r.location_country,
                  timezone: r.timezone,
                  city: r.city,
                  region: r.region,
                  path: r.path,
                  user_agent: r.user_agent,
                }}
              />
            </div>
          </div>
        )}
      />
    </>
  );
}
